/**
 * Grades Service for MyClass School Management System
 * Handles all academic grades-related data operations with role-based access
 */

import BaseService from './baseService';

class GradesService extends BaseService {
  constructor(tenantId = 'tn_01') {
    super(tenantId);
  }

  /**
   * Get comprehensive grades data with role-based filtering
   */
  async getGradesData(classId = null, subject = null, studentId = null, userRole = null) {
    try {
      this.logActivity('getGradesData', { classId, subject, studentId, userRole });

      // Build filters based on user role and parameters
      const filters = {};
      if (classId) filters.class_id = classId;
      if (subject) filters.subject = subject;
      if (studentId) filters.student_id = studentId;

      // Get grades records
      const { data: grades, error: gradesError } = await this.getData('student_grades', filters, {
        orderBy: 'created_at',
        ascending: false
      });

      if (gradesError) {
        throw gradesError;
      }

      // Get students for context
      const studentFilters = {};
      if (classId) studentFilters.class_id = classId;
      if (studentId) studentFilters.student_id = studentId;
      
      const { data: students, error: studentsError } = await this.getData('saas_students', studentFilters, {
        orderBy: 'name'
      });

      if (studentsError) {
        console.warn('Could not fetch students:', studentsError);
      }

      // Get classes for context
      const { data: classes, error: classesError } = await this.getData('saas_classes', {}, {
        orderBy: 'name'
      });

      if (classesError) {
        console.warn('Could not fetch classes:', classesError);
      }

      // Get subjects for context
      const { data: subjects, error: subjectsError } = await this.getData('subjects', {}, {
        orderBy: 'subject_name_zh'
      });

      if (subjectsError) {
        console.warn('Could not fetch subjects:', subjectsError);
      }

      // Process grades data with additional context
      const processedGrades = grades.map(grade => {
        const student = students?.find(s => s.student_id === grade.student_id);
        const classInfo = classes?.find(c => c.class_id === grade.class_id);
        const subjectInfo = subjects?.find(s => s.subject_name_zh === grade.subject);
        
        return {
          ...grade,
          student: student,
          class: classInfo,
          subjectInfo: subjectInfo,
          studentName: student?.name || 'Unknown Student',
          className: classInfo?.name || 'Unknown Class',
          gradeLevel: this.getGradeLevel(grade.grade),
          isPassing: this.isPassingGrade(grade.grade)
        };
      });

      // Calculate summary statistics
      const totalGrades = processedGrades.length;
      const passingGrades = processedGrades.filter(g => g.isPassing).length;
      const failingGrades = processedGrades.filter(g => !g.isPassing).length;
      const averageGrade = this.calculateAverageGrade(processedGrades);
      const highestGrade = Math.max(...processedGrades.map(g => g.grade || 0));
      const lowestGrade = Math.min(...processedGrades.map(g => g.grade || 0));

      // Group by subject
      const subjectGroups = this.groupGradesBySubject(processedGrades);

      // Group by class
      const classGroups = this.groupGradesByClass(processedGrades);

      // Group by student
      const studentGroups = this.groupGradesByStudent(processedGrades);

      // Get recent grades
      const recentGrades = processedGrades.slice(0, 10);

      const summaryData = {
        totalGrades,
        passingGrades,
        failingGrades,
        averageGrade,
        highestGrade,
        lowestGrade,
        grades: processedGrades,
        subjectGroups,
        classGroups,
        studentGroups,
        recentGrades,
        students: students || [],
        classes: classes || [],
        subjects: subjects || []
      };

      return { data: summaryData, error: null };

    } catch (err) {
      console.error('Error in getGradesData:', err);
      return { data: null, error: this.formatError(err) };
    }
  }

  /**
   * Get student academic performance
   */
  async getStudentAcademicPerformance(studentId) {
    try {
      this.logActivity('getStudentAcademicPerformance', { studentId });

      // Get all grades for the student
      const { data: grades, error: gradesError } = await this.getData('student_grades', {
        student_id: studentId
      }, {
        orderBy: 'created_at',
        ascending: false
      });

      if (gradesError) {
        throw gradesError;
      }

      // Get student information
      const { data: student, error: studentError } = await this.getData('saas_students', {
        student_id: studentId
      });

      if (studentError || !student || student.length === 0) {
        throw new Error('Student not found');
      }

      const studentData = student[0];

      // Get class information
      const { data: classData, error: classError } = await this.getData('saas_classes', {
        class_id: studentData.class_id
      });

      if (classError) {
        console.warn('Could not fetch class data:', classError);
      }

      // Calculate performance statistics
      const totalGrades = grades?.length || 0;
      const passingGrades = grades?.filter(g => this.isPassingGrade(g.grade)).length || 0;
      const failingGrades = totalGrades - passingGrades;
      const averageGrade = this.calculateAverageGrade(grades || []);
      const attendanceRate = await this.getStudentAttendanceRate(studentId);

      // Group by subject
      const subjectPerformance = this.groupGradesBySubject(grades || []);

      // Calculate grade distribution
      const gradeDistribution = this.calculateGradeDistribution(grades || []);

      // Get recent performance trend
      const recentGrades = grades?.slice(0, 10) || [];
      const performanceTrend = this.calculatePerformanceTrend(recentGrades);

      return {
        data: {
          student: studentData,
          class: classData?.[0] || null,
          totalGrades,
          passingGrades,
          failingGrades,
          averageGrade,
          attendanceRate,
          subjectPerformance,
          gradeDistribution,
          performanceTrend,
          recentGrades
        },
        error: null
      };

    } catch (err) {
      console.error('Error in getStudentAcademicPerformance:', err);
      return { data: null, error: this.formatError(err) };
    }
  }

  /**
   * Get class academic performance
   */
  async getClassAcademicPerformance(classId) {
    try {
      this.logActivity('getClassAcademicPerformance', { classId });

      // Get all grades for the class
      const { data: grades, error: gradesError } = await this.getData('student_grades', {
        class_id: classId
      }, {
        orderBy: 'created_at',
        ascending: false
      });

      if (gradesError) {
        throw gradesError;
      }

      // Get class information
      const { data: classData, error: classError } = await this.getData('saas_classes', {
        class_id: classId
      });

      if (classError || !classData || classData.length === 0) {
        throw new Error('Class not found');
      }

      const classInfo = classData[0];

      // Get students in the class
      const { data: students, error: studentsError } = await this.getData('saas_students', {
        class_id: classId
      }, {
        orderBy: 'name'
      });

      if (studentsError) {
        console.warn('Could not fetch students:', studentsError);
      }

      // Calculate class statistics
      const totalGrades = grades?.length || 0;
      const totalStudents = students?.length || 0;
      const averageGrade = this.calculateAverageGrade(grades || []);
      const passingRate = totalGrades > 0 ? Math.round((grades?.filter(g => this.isPassingGrade(g.grade)).length / totalGrades) * 100) : 0;

      // Group by subject
      const subjectPerformance = this.groupGradesBySubject(grades || []);

      // Calculate student rankings
      const studentRankings = this.calculateStudentRankings(grades || [], students || []);

      // Get top performers
      const topPerformers = studentRankings.slice(0, 5);

      // Get students needing attention
      const studentsNeedingAttention = studentRankings
        .filter(s => s.averageGrade < 60)
        .slice(0, 5);

      return {
        data: {
          class: classInfo,
          totalGrades,
          totalStudents,
          averageGrade,
          passingRate,
          subjectPerformance,
          studentRankings,
          topPerformers,
          studentsNeedingAttention
        },
        error: null
      };

    } catch (err) {
      console.error('Error in getClassAcademicPerformance:', err);
      return { data: null, error: this.formatError(err) };
    }
  }

  /**
   * Create or update grade
   */
  async saveGrade(gradeData) {
    try {
      this.logActivity('saveGrade', { gradeData });

      if (gradeData.id) {
        // Update existing grade
        const { data, error } = await this.updateRecord('student_grades', gradeData.id, gradeData);
        if (error) throw error;
        return { data, error: null };
      } else {
        // Create new grade
        const { data, error } = await this.createRecord('student_grades', gradeData);
        if (error) throw error;
        return { data, error: null };
      }
    } catch (err) {
      console.error('Error in saveGrade:', err);
      return { data: null, error: this.formatError(err) };
    }
  }

  /**
   * Delete grade
   */
  async deleteGrade(gradeId) {
    try {
      this.logActivity('deleteGrade', { gradeId });

      const { data, error } = await this.deleteRecord('student_grades', gradeId);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (err) {
      console.error('Error in deleteGrade:', err);
      return { data: null, error: this.formatError(err) };
    }
  }

  /**
   * Get grade statistics
   */
  async getGradeStatistics(classId = null, subject = null, startDate = null, endDate = null) {
    try {
      this.logActivity('getGradeStatistics', { classId, subject, startDate, endDate });

      const filters = {};
      if (classId) filters.class_id = classId;
      if (subject) filters.subject = subject;

      const { data: grades, error } = await this.getData('student_grades', filters);

      if (error) {
        throw error;
      }

      // Filter by date range if provided
      let filteredGrades = grades || [];
      if (startDate && endDate) {
        filteredGrades = grades?.filter(grade => {
          const gradeDate = new Date(grade.created_at);
          return gradeDate >= new Date(startDate) && gradeDate <= new Date(endDate);
        }) || [];
      }

      // Calculate overall statistics
      const totalGrades = filteredGrades.length;
      const averageGrade = this.calculateAverageGrade(filteredGrades);
      const passingRate = totalGrades > 0 ? Math.round((filteredGrades.filter(g => this.isPassingGrade(g.grade)).length / totalGrades) * 100) : 0;

      // Grade distribution
      const gradeDistribution = this.calculateGradeDistribution(filteredGrades);

      // Subject breakdown
      const subjectBreakdown = this.groupGradesBySubject(filteredGrades);

      // Class breakdown
      const classBreakdown = this.groupGradesByClass(filteredGrades);

      return {
        data: {
          period: { start: startDate, end: endDate },
          overall: {
            totalGrades,
            averageGrade,
            passingRate,
            gradeDistribution
          },
          subjectBreakdown: Object.values(subjectBreakdown),
          classBreakdown: Object.values(classBreakdown)
        },
        error: null
      };

    } catch (err) {
      console.error('Error in getGradeStatistics:', err);
      return { data: null, error: this.formatError(err) };
    }
  }

  /**
   * Group grades by subject
   */
  groupGradesBySubject(grades) {
    const subjectGroups = {};

    grades.forEach(grade => {
      const subject = grade.subject || 'Unknown';
      if (!subjectGroups[subject]) {
        subjectGroups[subject] = {
          subject,
          grades: [],
          totalGrades: 0,
          averageGrade: 0,
          passingCount: 0,
          failingCount: 0,
          passingRate: 0
        };
      }
      subjectGroups[subject].grades.push(grade);
      subjectGroups[subject].totalGrades++;
      if (this.isPassingGrade(grade.grade)) {
        subjectGroups[subject].passingCount++;
      } else {
        subjectGroups[subject].failingCount++;
      }
    });

    // Calculate averages and rates
    Object.values(subjectGroups).forEach(group => {
      group.averageGrade = this.calculateAverageGrade(group.grades);
      group.passingRate = group.totalGrades > 0 ? Math.round((group.passingCount / group.totalGrades) * 100) : 0;
    });

    return subjectGroups;
  }

  /**
   * Group grades by class
   */
  groupGradesByClass(grades) {
    const classGroups = {};

    grades.forEach(grade => {
      const classId = grade.class_id;
      if (!classGroups[classId]) {
        classGroups[classId] = {
          classId,
          className: grade.className || 'Unknown Class',
          grades: [],
          totalGrades: 0,
          averageGrade: 0,
          passingCount: 0,
          failingCount: 0,
          passingRate: 0
        };
      }
      classGroups[classId].grades.push(grade);
      classGroups[classId].totalGrades++;
      if (this.isPassingGrade(grade.grade)) {
        classGroups[classId].passingCount++;
      } else {
        classGroups[classId].failingCount++;
      }
    });

    // Calculate averages and rates
    Object.values(classGroups).forEach(group => {
      group.averageGrade = this.calculateAverageGrade(group.grades);
      group.passingRate = group.totalGrades > 0 ? Math.round((group.passingCount / group.totalGrades) * 100) : 0;
    });

    return classGroups;
  }

  /**
   * Group grades by student
   */
  groupGradesByStudent(grades) {
    const studentGroups = {};

    grades.forEach(grade => {
      const studentId = grade.student_id;
      if (!studentGroups[studentId]) {
        studentGroups[studentId] = {
          studentId,
          studentName: grade.studentName || 'Unknown Student',
          grades: [],
          totalGrades: 0,
          averageGrade: 0,
          passingCount: 0,
          failingCount: 0,
          passingRate: 0
        };
      }
      studentGroups[studentId].grades.push(grade);
      studentGroups[studentId].totalGrades++;
      if (this.isPassingGrade(grade.grade)) {
        studentGroups[studentId].passingCount++;
      } else {
        studentGroups[studentId].failingCount++;
      }
    });

    // Calculate averages and rates
    Object.values(studentGroups).forEach(group => {
      group.averageGrade = this.calculateAverageGrade(group.grades);
      group.passingRate = group.totalGrades > 0 ? Math.round((group.passingCount / group.totalGrades) * 100) : 0;
    });

    return studentGroups;
  }

  /**
   * Calculate average grade
   */
  calculateAverageGrade(grades) {
    if (!grades || grades.length === 0) return 0;
    
    const validGrades = grades.filter(g => g.grade && !isNaN(g.grade));
    if (validGrades.length === 0) return 0;
    
    const sum = validGrades.reduce((total, grade) => total + grade.grade, 0);
    return Math.round((sum / validGrades.length) * 100) / 100;
  }

  /**
   * Check if grade is passing
   */
  isPassingGrade(grade) {
    return grade && !isNaN(grade) && grade >= 60;
  }

  /**
   * Get grade level
   */
  getGradeLevel(grade) {
    if (!grade || isNaN(grade)) return 'Unknown';
    
    if (grade >= 90) return 'A';
    if (grade >= 80) return 'B';
    if (grade >= 70) return 'C';
    if (grade >= 60) return 'D';
    return 'F';
  }

  /**
   * Calculate grade distribution
   */
  calculateGradeDistribution(grades) {
    const distribution = {
      'A': 0, 'B': 0, 'C': 0, 'D': 0, 'F': 0
    };

    grades.forEach(grade => {
      const level = this.getGradeLevel(grade.grade);
      if (distribution.hasOwnProperty(level)) {
        distribution[level]++;
      }
    });

    return distribution;
  }

  /**
   * Calculate student rankings
   */
  calculateStudentRankings(grades, students) {
    const studentAverages = {};

    // Calculate average for each student
    students.forEach(student => {
      const studentGrades = grades.filter(g => g.student_id === student.student_id);
      studentAverages[student.student_id] = {
        studentId: student.student_id,
        studentName: student.name,
        averageGrade: this.calculateAverageGrade(studentGrades),
        totalGrades: studentGrades.length
      };
    });

    // Sort by average grade
    return Object.values(studentAverages)
      .sort((a, b) => b.averageGrade - a.averageGrade)
      .map((student, index) => ({
        ...student,
        rank: index + 1
      }));
  }

  /**
   * Calculate performance trend
   */
  calculatePerformanceTrend(recentGrades) {
    if (recentGrades.length < 2) return 'stable';

    const sortedGrades = recentGrades
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map(g => g.grade);

    const firstHalf = sortedGrades.slice(0, Math.floor(sortedGrades.length / 2));
    const secondHalf = sortedGrades.slice(Math.floor(sortedGrades.length / 2));

    const firstAvg = this.calculateAverageGrade(firstHalf.map(g => ({ grade: g })));
    const secondAvg = this.calculateAverageGrade(secondHalf.map(g => ({ grade: g })));

    const difference = secondAvg - firstAvg;

    if (difference > 5) return 'improving';
    if (difference < -5) return 'declining';
    return 'stable';
  }

  /**
   * Get student attendance rate (placeholder)
   */
  async getStudentAttendanceRate(studentId) {
    // This would typically fetch from attendance records
    // For now, return a mock value
    return 95.2;
  }
}

export default GradesService;
